import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Duet
 * -----------------------------------------------------------
 * Tap-through "Did you know?" teaser. Sounds are synthesised with the
 * Web Audio API (no audio files). Everything is wrapped in try/catch so the
 * section still works silently when Web Audio is unavailable.
 */
const EASE = [0.22, 1, 0.36, 1];

const FADE_LINE = {
  background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 50%, rgba(201,169,97,0) 100%)",
};

const VIEWPORT = { once: true, margin: "-60px" };
const GOLD = "#C9A961";
const FACT_CARD =
  "relative flex flex-col items-center justify-start text-center px-3 py-5 sm:px-6 sm:py-7 rounded-2xl border border-gold bg-ivory shadow-[0_14px_30px_-18px_rgba(58,10,24,0.35)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gold";

/* ---------- audio ---------- */
let ctx = null;
let master = null;

function getCtx() {
  try {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.25;
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

function hum(c, freq = 440, when = 0) {
  const t = c.currentTime + when;
  const osc = c.createOscillator();
  const lfo = c.createOscillator();
  const lfoGain = c.createGain();
  const env = c.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  lfo.frequency.value = 5;
  lfoGain.gain.value = freq * 0.012;
  lfo.connect(lfoGain);
  lfoGain.connect(osc.frequency);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(0.7, t + 0.45);
  env.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
  osc.connect(env);
  env.connect(master);
  osc.start(t);
  lfo.start(t);
  osc.stop(t + 1.3);
  lfo.stop(t + 1.3);
}

// E major-ish open chord, low string to high
const STRINGS = [82.41, 123.47, 164.81, 207.65, 246.94, 329.63];

function strum(c, when = 0) {
  const base = c.currentTime + when;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 1800;
  lp.connect(master);
  STRINGS.forEach((f, i) => {
    const t = base + i * 0.025;
    const osc = c.createOscillator();
    const env = c.createGain();
    osc.type = i % 2 ? "triangle" : "sawtooth";
    osc.frequency.value = f;
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(0.22, t + 0.012);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    osc.connect(env);
    env.connect(lp);
    osc.start(t);
    osc.stop(t + 1.6);
  });
}

function playStep(step) {
  try {
    const c = getCtx();
    if (!c) return;
    if (step === 0) hum(c, 440);
    else if (step === 1) strum(c);
    else {
      strum(c);
      hum(c, 329.63, 0.1);
    }
  } catch {
    /* silent */
  }
}

/* ---------- falling notes ---------- */
function Notes() {
  const reduce = useReducedMotion();
  const notes = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        ch: i % 2 ? "♫" : "♪",
        left: 4 + ((i * 53) % 92),
        size: 18 + ((i * 7) % 16),
        delay: (i % 7) * 0.35,
        dur: 4.5 + ((i * 3) % 5) * 0.6,
        sway: (i % 2 ? 1 : -1) * (10 + (i % 4) * 6),
      })),
    []
  );
  if (reduce) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {notes.map((n, i) => (
        <motion.span
          key={i}
          className="absolute top-0 text-gold select-none"
          style={{ left: `${n.left}%`, fontSize: n.size }}
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 460, x: [0, n.sway, 0], opacity: [0, 0.9, 0.9, 0], rotate: [0, n.sway, 0] }}
          transition={{ duration: n.dur, delay: n.delay, ease: "easeIn" }}
        >
          {n.ch}
        </motion.span>
      ))}
    </div>
  );
}

/* ---------- scratch cover ---------- */
const BRUSH = 36;
const CLEAR_TARGET = 0.45;

function paintCover(ctx2, w, h) {
  const g = ctx2.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#E8CC7E");
  g.addColorStop(0.5, "#C9A961");
  g.addColorStop(1, "#B8934A");
  ctx2.globalCompositeOperation = "source-over";
  ctx2.fillStyle = g;
  ctx2.fillRect(0, 0, w, h);
  // diagonal sheen
  const sh = ctx2.createLinearGradient(0, h, w, 0);
  sh.addColorStop(0, "rgba(255,255,255,0)");
  sh.addColorStop(0.42, "rgba(255,255,255,0)");
  sh.addColorStop(0.5, "rgba(255,255,255,0.28)");
  sh.addColorStop(0.58, "rgba(255,255,255,0)");
  sh.addColorStop(1, "rgba(255,255,255,0)");
  ctx2.fillStyle = sh;
  ctx2.fillRect(0, 0, w, h);
  ctx2.textAlign = "center";
  ctx2.textBaseline = "middle";
  ctx2.fillStyle = "#8A0F36";
  ctx2.font = "16px 'Plus Jakarta Sans', sans-serif";
  ctx2.fillText("\u2726", w / 2, h / 2 - 22);
  ctx2.font = "600 13px 'Plus Jakarta Sans', sans-serif";
  if ("letterSpacing" in ctx2) ctx2.letterSpacing = "3px";
  ctx2.fillText("SCRATCH TO REVEAL", w / 2, h / 2 + 4);
  if ("letterSpacing" in ctx2) ctx2.letterSpacing = "0px";
}

function ScratchCover({ revealed, onReveal }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const lastCheck = useRef(0);
  const size = useRef({ w: 0, h: 0 });
  const revealedRef = useRef(revealed);
  const onRevealRef = useRef(onReveal);
  const scratched = useRef(false);
  const [ready, setReady] = useState(false);
  const [gone, setGone] = useState(false);
  revealedRef.current = revealed;
  onRevealRef.current = onReveal;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    let alive = true;
    const getC = () => canvas.getContext("2d", { willReadFrequently: true });
    const paint = (force) => {
      if (!alive || drawing.current) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      if (!force && w === size.current.w && h === size.current.h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nw = Math.round(w * dpr);
      const nh = Math.round(h * dpr);
      let old = null;
      if (scratched.current && canvas.width && canvas.height) {
        old = document.createElement("canvas");
        old.width = canvas.width;
        old.height = canvas.height;
        old.getContext("2d").drawImage(canvas, 0, 0);
      }
      size.current = { w, h };
      canvas.width = nw;
      canvas.height = nh;
      const c = getC();
      if (old) {
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.drawImage(old, 0, 0, old.width, old.height, 0, 0, nw, nh);
        c.setTransform(dpr, 0, 0, dpr, 0, 0);
      } else {
        c.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintCover(c, w, h);
      }
    };
    paint(false);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (alive && !scratched.current) paint(true);
      });
    }
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => paint(false));
      ro.observe(canvas);
    }
    const t = setTimeout(() => alive && setReady(true), 650);
    return () => {
      alive = false;
      clearTimeout(t);
      if (ro) ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!revealed) return undefined;
    const t = setTimeout(() => setGone(true), 550);
    return () => clearTimeout(t);
  }, [revealed]);

  const check = () => {
    const canvas = ref.current;
    if (!canvas || revealedRef.current) return;
    try {
      const c = canvas.getContext("2d", { willReadFrequently: true });
      const { width: w, height: h } = canvas;
      const data = c.getImageData(0, 0, w, h).data;
      const stepPx = 8;
      let total = 0;
      let cleared = 0;
      for (let y = 0; y < h; y += stepPx) {
        for (let x = 0; x < w; x += stepPx) {
          total += 1;
          if (data[(y * w + x) * 4 + 3] < 128) cleared += 1;
        }
      }
      if (total && cleared / total >= CLEAR_TARGET) onRevealRef.current();
    } catch {
      /* canvas read blocked: the Reveal button remains */
    }
  };

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const stroke = (from, to) => {
    const c = ref.current.getContext("2d", { willReadFrequently: true });
    c.globalCompositeOperation = "destination-out";
    c.lineCap = "round";
    c.lineJoin = "round";
    c.lineWidth = BRUSH;
    c.beginPath();
    c.moveTo(from.x, from.y);
    c.lineTo(to.x, to.y);
    c.stroke();
  };

  const onDown = (e) => {
    if (revealedRef.current) return;
    drawing.current = true;
    scratched.current = true;
    try {
      ref.current.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const p = pos(e);
    last.current = p;
    stroke(p, { x: p.x + 0.01, y: p.y });
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    stroke(last.current, p);
    last.current = p;
    const now = performance.now();
    if (now - lastCheck.current > 150) {
      lastCheck.current = now;
      check();
    }
  };

  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    check();
  };

  if (gone) return null;
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      className="absolute inset-0 z-20 h-full w-full rounded-3xl cursor-pointer"
      style={{
        touchAction: "none",
        opacity: revealed ? 0 : 1,
        transition: "opacity 0.5s ease",
        pointerEvents: revealed || !ready ? "none" : "auto",
      }}
    />
  );
}

export default function Duet() {
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const reduce = useReducedMotion();
  const done = revealed || !!reduce;
  const reduceRef = useRef(false);
  reduceRef.current = !!reduce;
  const lock = useRef(false);
  const stepRef = useRef(0); // read by the exiting card too, so it never acts on a stale step
  const timer = useRef(null);
  const revealedRef = useRef(false);
  stepRef.current = step;
  useEffect(() => () => clearTimeout(timer.current), []);

  const advance = useCallback(() => {
    if (lock.current || stepRef.current > 0) return;
    lock.current = true;
    // hold the lock for the whole flip (0.6s out + 0.6s in)
    timer.current = setTimeout(() => (lock.current = false), 1250);
    if (reduceRef.current) playStep(2);
    setStep(1);
  }, []);

  const reveal = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealed(true);
    playStep(2);
  }, []);

  const reset = () => {
    revealedRef.current = false;
    setRevealed(false);
    setStep(0);
    setRun((r) => r + 1);
  };

  const variants = {
    enter: { opacity: 0, rotateY: -70, y: 12 },
    center: { opacity: 1, rotateY: 0, y: 0 },
    exit: { opacity: 0, rotateY: 70, y: -12, pointerEvents: "none" },
  };
  const trans = { duration: 0.6, ease: EASE };
  // scroll-triggered entrance; shown immediately under reduced motion
  const appear = (delay, from, to, duration = 0.6) =>
    reduce
      ? {}
      : {
          initial: from,
          whileInView: to,
          viewport: VIEWPORT,
          transition: { delay, duration, ease: EASE },
        };
  const CARD =
    "relative w-full min-h-[440px] flex flex-col items-center justify-center text-center px-6 py-10 rounded-3xl border border-gold bg-ivory shadow-[0_20px_40px_-20px_rgba(58,10,24,0.35)]";

  return (
    <div id="duet" className="relative mt-10 text-center">
      <div className="overline text-rani mb-4">Did you know?</div>
      <div className="mx-auto mb-6 h-px w-32" style={FADE_LINE} />

      <div className="relative mx-auto w-full max-w-md" style={{ perspective: 1200 }}>
        <div aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            {step === 0 ? (
              <motion.div
                key={`s${run}-flow`}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={trans}
                className="w-full sm:-mx-12 sm:w-[calc(100%+6rem)]"
              >
                <div className="grid grid-cols-2 gap-3 sm:gap-6">
                  <motion.button
                    type="button"
                    onClick={() => playStep(0)}
                    className={FACT_CARD}
                    {...appear(0, { opacity: 0, y: 24 }, { opacity: 1, y: 0 })}
                  >
                    <span className="text-lg leading-none text-gold" aria-hidden="true">{"\u266A"}</span>
                    <span className="mt-2 font-display text-xl sm:text-3xl text-rani leading-tight">
                      Sona is a beautiful singer
                    </span>
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => playStep(1)}
                    className={FACT_CARD}
                    {...appear(0.25, { opacity: 0, y: 24 }, { opacity: 1, y: 0 })}
                  >
                    <span className="text-lg leading-none text-gold" aria-hidden="true">{"\u266B"}</span>
                    <span className="mt-2 font-display text-xl sm:text-3xl text-rani leading-tight">
                      Shubh is an amazing guitarist
                    </span>
                  </motion.button>
                </div>

                {/* connector: drops sit at the card centres (svg overhangs by half the gap) */}
                <div className="relative -mx-1.5 sm:-mx-3 h-14" aria-hidden="true">
                  {/* plain gold lines (not an svg path draw, which could stop partway
                      when stretched): two drops at 25% / 75%, a bar between them,
                      and a stem down from the middle, each growing into place */}
                  <motion.span
                    className="absolute left-1/4 top-0 h-[26px] w-[1.5px] -ml-px origin-top"
                    style={{ background: GOLD }}
                    {...appear(0.6, { scaleY: 0 }, { scaleY: 1 }, 0.3)}
                  />
                  <motion.span
                    className="absolute left-3/4 top-0 h-[26px] w-[1.5px] -ml-px origin-top"
                    style={{ background: GOLD }}
                    {...appear(0.6, { scaleY: 0 }, { scaleY: 1 }, 0.3)}
                  />
                  <motion.span
                    className="absolute left-1/4 right-1/4 top-[25px] h-[1.5px] origin-center"
                    style={{ background: GOLD }}
                    {...appear(0.9, { scaleX: 0 }, { scaleX: 1 }, 0.3)}
                  />
                  <motion.span
                    className="absolute left-1/2 top-[25px] bottom-[7px] w-[1.5px] -ml-px origin-top"
                    style={{ background: GOLD }}
                    {...appear(1.2, { scaleY: 0 }, { scaleY: 1 }, 0.2)}
                  />
                  <motion.svg
                    className="absolute bottom-0 left-1/2 -ml-[5px]"
                    width="10"
                    height="8"
                    viewBox="0 0 10 8"
                    {...appear(1.3, { opacity: 0 }, { opacity: 1 }, 0.3)}
                  >
                    <polygon points="0,0 10,0 5,8" fill={GOLD} />
                  </motion.svg>
                </div>

                <motion.div
                  className="mx-auto flex w-full max-w-[300px] flex-col items-center rounded-2xl border border-gold bg-ivory px-6 py-6 shadow-[0_14px_30px_-18px_rgba(58,10,24,0.35)]"
                  {...appear(1.4, { opacity: 0, y: 16 }, { opacity: 1, y: 0 })}
                >
                  <span className="font-display text-2xl sm:text-4xl text-rani leading-tight">So what happens next?</span>
                  <button
                    type="button"
                    onClick={advance}
                    className="mt-5 cursor-pointer rounded-full border border-gold/60 px-4 py-1.5 text-[10px] uppercase tracking-chip text-gold-deep font-semibold hover:text-rani transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  >
                    Next {"\u2192"}
                  </button>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key={`reveal-${run}`}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={trans}
                className={`${CARD} overflow-hidden`}
              >
                {done && <Notes />}
                <div className="relative z-10 flex flex-col items-center">
                  <h2 className="font-display text-5xl sm:text-6xl text-rani leading-[1.05]">A lifetime duet!</h2>
                  {/* lining figures so "21" and "2026" stand as tall as the letters */}
                  <p className="mt-3 font-display text-[26px] sm:text-4xl text-gold-deep leading-tight [font-variant-numeric:lining-nums]">
                    Starting <span className="whitespace-nowrap">21 November 2026</span>
                  </p>
                  <img
                    src="/story/duet.jpg"
                    alt="Sona singing and Shubh playing the guitar"
                    className="mt-5 w-[200px] sm:w-[240px] aspect-[640/853] object-cover rounded-xl border border-gold shadow-[0_12px_26px_-14px_rgba(58,10,24,0.45)]"
                    draggable="false"
                  />
                  <p className="mt-2 font-display italic text-2xl text-gold">#SonaKaRaj</p>
                </div>
                {!reduce && <ScratchCover revealed={revealed} onReveal={reveal} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2.5" aria-hidden="true">
          {[0, 1].map((i) => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full border border-gold transition-colors duration-500 ${
                i === step ? "bg-gold" : "bg-transparent"
              }`}
            />
          ))}
        </div>

        <div className="mt-4 flex h-6 items-center justify-center gap-6">
          {step === 1 && !done && (
            <button
              type="button"
              onClick={reveal}
              className="text-[10px] uppercase tracking-chip text-gold-deep font-semibold hover:text-rani transition-colors"
            >
              Reveal
            </button>
          )}
          {step === 1 && (
            <button
              type="button"
              onClick={reset}
              className="text-[10px] uppercase tracking-chip text-gold-deep font-semibold hover:text-rani transition-colors"
            >
              Play again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
